import _extends from "@babel/runtime/helpers/esm/extends";
import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import React, { cloneElement, forwardRef, isValidElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import classNames from 'classnames/bind';
import { CurrencySelect } from "../../CurrencySelect";
import { LanguageSelect } from "../../LanguageSelect";
import Popover from "../../Popover";
import Scrollbar from "../../Scrollbar";
import { defaultPropArray, defaultPropObject } from "../../constants";
import { useMediaQuery, useWindowSize } from "../../hooks";
import { ArrowLeft, Burger, ChevronSmallUp, CloseLinear, Oneworld, Search, ShoppingBagFilled, UserLinear } from "../../icons";
import { ButtonBase } from "../../internal/ButtonBase";
import { ClickOutside } from "../../internal/ClickOutside";
import { TextBase } from "../../internal/TextBase";
import { getMethodsForTestId } from "../../utils/getMethodsForTestId";
import noop from "../../utils/noop";
import { isFunc } from "../../utils/type";
import { HeaderSearch, Menu, MobileMenu, NavLinks } from './components';
import { Favorites } from './components/Favorites';
import { currencies as defaultCurrencyCodes } from './components/RegionSelect/constants';
import { AVATARS, CURRENCY_STYLE, LOGOS, MOBILE_LOGOS, themes } from './constants';
import { useHeader } from './useHeader';
import styles from './HeaderCanary.css';
var cx = classNames.bind(styles);

var getNewTabProps = function getNewTabProps(predicate) {
  return predicate ? {
    target: '_blank',
    rel: 'noopener noreferrer'
  } : {};
};

export var HeaderCanary = /*#__PURE__*/forwardRef(function Header(props, ref) {
  var _navigationFavorites$, _navigation$currentPo, _navigation$currentPo2;

  var _useHeader = useHeader(props),
      avatar = _useHeader.avatar,
      backLinkClose = _useHeader.backLinkClose,
      _useHeader$backLinkTi = _useHeader.backLinkTitle,
      backLinkTitle = _useHeader$backLinkTi === void 0 ? 'Назад' : _useHeader$backLinkTi,
      cartCount = _useHeader.cartCount,
      cartTitle = _useHeader.cartTitle,
      _useHeader$currency = _useHeader.currency,
      currency = _useHeader$currency === void 0 ? 'RUB' : _useHeader$currency,
      _useHeader$currencies = _useHeader.currencies,
      currencies = _useHeader$currencies === void 0 ? defaultCurrencyCodes : _useHeader$currencies,
      currencyTitle = _useHeader.currencyTitle,
      _useHeader$headerTitl = _useHeader.headerTitle,
      headerTitle = _useHeader$headerTitl === void 0 ? 'S7 Airlines | Купить авиабилеты онлайн' : _useHeader$headerTitl,
      hideCurrency = _useHeader.hideCurrency,
      hideRegion = _useHeader.hideRegion,
      login = _useHeader.login,
      _useHeader$language = _useHeader.language,
      language = _useHeader$language === void 0 ? 'ru' : _useHeader$language,
      languageSelectProps = _useHeader.languageSelectProps,
      langs = _useHeader.languages,
      linkBack = _useHeader.linkBack,
      linkHome = _useHeader.linkHome,
      linkHomeInNewTab = _useHeader.linkHomeInNewTab,
      linkHomeProps = _useHeader.linkHomeProps,
      linkOneWorld = _useHeader.linkOneWorld,
      linkOneWorldInNewTab = _useHeader.linkOneWorldInNewTab,
      LoginForm = _useHeader.LoginForm,
      _useHeader$loginTitle = _useHeader.loginTitle,
      loginTitle = _useHeader$loginTitle === void 0 ? 'Войти' : _useHeader$loginTitle,
      loginTitleProps = _useHeader.loginTitleProps,
      mobileMenuHint = _useHeader.mobileMenuHint,
      mobileMenuHome = _useHeader.mobileMenuHome,
      navigation = _useHeader.navigation,
      _useHeader$navigation = _useHeader.navigationFavorites,
      navigationFavorites = _useHeader$navigation === void 0 ? defaultPropObject : _useHeader$navigation,
      oneWorldLink = _useHeader.oneWorldLink,
      _useHeader$openMenuTi = _useHeader.openMenuTitle,
      openMenuTitle = _useHeader$openMenuTi === void 0 ? 'Открыть меню' : _useHeader$openMenuTi,
      ProfileInfo = _useHeader.ProfileInfo,
      searchProps = _useHeader.searchProps,
      showSearch = _useHeader.showSearch,
      showMobileAddons = _useHeader.showMobileAddons,
      _useHeader$theme = _useHeader.theme,
      theme = _useHeader$theme === void 0 ? themes.b2c : _useHeader$theme,
      togglerProps = _useHeader.togglerProps,
      rightSide = _useHeader.rightSide,
      logo = _useHeader.logo,
      fixed = _useHeader.fixed,
      namespace = _useHeader.namespace,
      isUserInfoOpen = _useHeader.isUserInfoOpen,
      isSearch = _useHeader.isSearch,
      searchValue = _useHeader.searchValue,
      searchLoading = _useHeader.searchLoading,
      searchPlaceholder = _useHeader.searchPlaceholder,
      searchNotFoundTitle = _useHeader.searchNotFoundTitle,
      searchNotFoundSubtitle = _useHeader.searchNotFoundSubtitle,
      _useHeader$navigation2 = _useHeader.navigationResult,
      navigationResult = _useHeader$navigation2 === void 0 ? defaultPropArray : _useHeader$navigation2,
      searchResults = _useHeader.searchResults,
      searchNotFound = _useHeader.searchNotFound,
      searchButtonProps = _useHeader.searchButtonProps,
      mobileSelectedGroup = _useHeader.mobileSelectedGroup,
      _useHeader$onMenuOpen = _useHeader.onMenuOpen,
      onMenuOpen = _useHeader$onMenuOpen === void 0 ? noop : _useHeader$onMenuOpen,
      setIsUserInfoOpen = _useHeader.setIsUserInfoOpen,
      _useHeader$onChangeCu = _useHeader.onChangeCurrency,
      onChangeCurrency = _useHeader$onChangeCu === void 0 ? noop : _useHeader$onChangeCu,
      _useHeader$onChangeLa = _useHeader.onChangeLanguage,
      onChangeLanguage = _useHeader$onChangeLa === void 0 ? noop : _useHeader$onChangeLa,
      onClickBack = _useHeader.onClickBack,
      onClickCart = _useHeader.onClickCart,
      onLoginClick = _useHeader.onLoginClick,
      _useHeader$onMenuClos = _useHeader.onMenuClose,
      onMenuClose = _useHeader$onMenuClos === void 0 ? noop : _useHeader$onMenuClos,
      _useHeader$onNavLinkC = _useHeader.onNavLinkChange,
      onNavLinkChange = _useHeader$onNavLinkC === void 0 ? noop : _useHeader$onNavLinkC,
      handleCloseSearch = _useHeader.handleCloseSearch,
      handleToggleSearch = _useHeader.handleToggleSearch,
      _useHeader$handleChan = _useHeader.handleChangeSearch,
      handleChangeSearch = _useHeader$handleChan === void 0 ? noop : _useHeader$handleChan,
      setMobileSubMenu = _useHeader.setMobileSubMenu,
      _useHeader$handleOnSu = _useHeader.handleOnSubmit,
      handleOnSubmit = _useHeader$handleOnSu === void 0 ? noop : _useHeader$handleOnSu,
      logoIcon = _useHeader.logoIcon,
      mobileLogoIcon = _useHeader.mobileLogoIcon,
      _useHeader$classes = _useHeader.classes,
      classes = _useHeader$classes === void 0 ? defaultPropObject : _useHeader$classes;

  var _getMethodsForTestId = getMethodsForTestId(namespace, 'header'),
      getTestId = _getMethodsForTestId.getTestId,
      getNamespace = _getMethodsForTestId.getNamespace;

  var _useState = useState(null),
      _useState2 = _slicedToArray(_useState, 2),
      currentPopup = _useState2[0],
      setCurrentPopup = _useState2[1];

  var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      showDropdownMenu = _useState4[0],
      setShowDropdownMenu = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      isMobileMenu = _useState6[0],
      setIsMobileMenu = _useState6[1];

  var _useState7 = useState(false),
      _useState8 = _slicedToArray(_useState7, 2),
      isNavLinksOpen = _useState8[0],
      setIsNavLinksOpen = _useState8[1];

  var _useState9 = useState(0),
      _useState10 = _slicedToArray(_useState9, 2),
      fixedHeaderHeight = _useState10[0],
      setFixedHeaderHeight = _useState10[1];

  var _useState11 = useState(0),
      _useState12 = _slicedToArray(_useState11, 2),
      headerHeight = _useState12[0],
      setHeaderHeight = _useState12[1];

  var _useWindowSize = useWindowSize(),
      windowWidth = _useWindowSize.width;

  var breakpoints = useMediaQuery('xs', 'lg', 'mdMax', 'sm', 'md');
  var isMobile = breakpoints.xs;
  var isDesktop = breakpoints.lg;
  var isTablet = breakpoints.mdMax;
  var isHideMobileMenuHeader = breakpoints.sm || breakpoints.md;
  var mobileMenuRef = useRef(null);
  var headerRef = useRef(ref);
  var contentRef = useRef(null);
  var closeNavPopup = useCallback(function (e) {
    if (e.target.type === 'button') {
      return;
    }

    if (onMenuClose) {
      onMenuClose();
    }

    setIsNavLinksOpen(false);
    setShowDropdownMenu(false);
  }, [onMenuClose]);

  var _ref = currentPopup && (navigation === null || navigation === void 0 ? void 0 : navigation[currentPopup]) || {},
      _ref$favorites = _ref.favorites,
      favorites = _ref$favorites === void 0 ? [] : _ref$favorites;

  var handleMenuOpen = function handleMenuOpen() {
    if (isMobileMenu) {
      handleMenuClose();
    } else {
      document.documentElement.classList.add(cx('html_ds_header_mobile_menu'));
      setIsMobileMenu(true);

      if (onMenuOpen) {
        onMenuOpen();
      }
    }
  };

  var handleMenuClose = useCallback(function () {
    document.documentElement.classList.remove(cx('html_ds_header_mobile_menu'));
    setIsMobileMenu(false);
    onMenuClose();
  }, [onMenuClose]);
  var toggleNavLinks = useCallback(function () {
    setIsNavLinksOpen(function (prev) {
      var newValue = !prev;
      setCurrentPopup(newValue ? 0 : null);
      setShowDropdownMenu(newValue);
      return newValue;
    });

    if (onMenuClose) {
      onMenuClose();
    }
  }, [onMenuClose]);

  var handleMenuToggle = function handleMenuToggle() {
    if (isMobile || theme === 'b2b') {
      return handleMenuOpen();
    }

    return toggleNavLinks();
  };

  var handleItemClick = useCallback(function (index) {
    setCurrentPopup(index);
    onNavLinkChange(index);

    if (currentPopup === index && showDropdownMenu) {
      if (onMenuClose) {
        onMenuClose();
      }

      setShowDropdownMenu(false);
    }

    if (!showDropdownMenu) {
      setShowDropdownMenu(true);

      if (onMenuOpen) {
        onMenuOpen();
      }
    }
  }, [currentPopup, onMenuClose, onMenuOpen, showDropdownMenu, onNavLinkChange]);
  useEffect(function () {
    if (!isMobile && isMobileMenu && theme !== 'b2b') {
      handleMenuClose();
    }

    if (isMobile && Number.isInteger(currentPopup)) {
      toggleNavLinks();
    }
  }, [isTablet, isMobile, isNavLinksOpen, toggleNavLinks, isDesktop, currentPopup, handleMenuClose, isMobileMenu, theme]);
  var LinkBack = useMemo(function () {
    return /*#__PURE__*/React.createElement("a", {
      className: cx('button'),
      href: linkBack,
      title: backLinkTitle
    }, /*#__PURE__*/React.createElement(ArrowLeft, null));
  }, [linkBack, backLinkTitle]);
  var BackArrow = useMemo(function () {
    if (backLinkClose && !isMobile) {
      return null;
    }

    if (linkBack) {
      return LinkBack;
    }

    if (onClickBack) {
      return /*#__PURE__*/React.createElement("button", _extends({
        type: "button",
        className: cx('button'),
        onClick: onClickBack,
        title: backLinkTitle
      }, getTestId('back', 'button')), backLinkClose ? /*#__PURE__*/React.createElement(CloseLinear, null) : /*#__PURE__*/React.createElement(ArrowLeft, null));
    }

    return null;
  }, [backLinkClose, isMobile, linkBack, onClickBack, LinkBack, backLinkTitle, getTestId]);
  var CartButton = useMemo(function () {
    if (!onClickCart) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section', 'section_cart')
    }, /*#__PURE__*/React.createElement("button", {
      type: "button",
      className: cx('cart_button', {
        cart_button_empty: !cartCount
      }, classes.rightIcon),
      onClick: onClickCart,
      title: cartTitle
    }, /*#__PURE__*/React.createElement(ShoppingBagFilled, null), cartCount && /*#__PURE__*/React.createElement("span", {
      className: cx('pointer')
    }, cartCount)));
  }, [onClickCart, cartCount, classes.rightIcon, cartTitle]);
  var Avatar = avatar || theme !== 'shop' && theme !== 'travel' && AVATARS[theme] || AVATARS.b2c;
  var Logo = logoIcon || LOGOS[theme];
  var LogoMobile = mobileLogoIcon || MOBILE_LOGOS[theme];
  var togglerContent;

  if (login) {
    togglerContent = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: cx('login_icon', classes.rightIcon)
    }, /*#__PURE__*/React.createElement(UserLinear, null)), /*#__PURE__*/React.createElement(TextBase, _extends({
      weight: "semibold",
      color: "N300",
      classes: {
        root: classes.rightIcon
      }
    }, loginTitleProps), loginTitle));
  } else {
    togglerContent = /*#__PURE__*/React.createElement(Avatar, {
      height: 32,
      width: 32
    });
  }

  var toggler = /*#__PURE__*/React.createElement("div", _extends({
    className: cx('login')
  }, togglerProps), togglerContent);
  var languageBase = /*#__PURE__*/React.createElement(LanguageSelect, _extends({
    align: "right",
    language: language,
    onChange: onChangeLanguage,
    languages: langs
  }, languageSelectProps));

  var Language = function Language() {
    if (!language) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section')
    }, languageBase);
  };

  var Currency = function Currency() {
    if (!currency || hideCurrency) {
      return null;
    }

    return /*#__PURE__*/React.createElement("div", {
      className: cx('section')
    }, /*#__PURE__*/React.createElement(CurrencySelect, {
      align: "right",
      currency: currency,
      currencies: currencies,
      title: currencyTitle,
      onChange: onChangeCurrency,
      style: CURRENCY_STYLE,
      classes: {
        root: classes.rightIcon
      }
    }));
  };

  var Region = function Region() {
    return /*#__PURE__*/React.createElement(React.Fragment, null, !hideCurrency && /*#__PURE__*/React.createElement(Currency, null), !hideRegion && /*#__PURE__*/React.createElement(Language, null));
  };

  useEffect(function () {
    if (headerRef.current) {
      var _headerRef$current;

      setFixedHeaderHeight((_headerRef$current = headerRef.current) === null || _headerRef$current === void 0 ? void 0 : _headerRef$current.clientHeight);
    }
  }, [fixed, windowWidth]);
  useEffect(function () {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.clientHeight);
    }
  }, [windowWidth]);
  var tabletMobileMenuStyles = isHideMobileMenuHeader ? {
    top: headerHeight,
    maxWidth: 375
  } : {};
  var ignoreRefs = useMemo(function () {
    return [headerRef];
  }, []);
  var profileBlock = /*#__PURE__*/React.createElement(React.Fragment, null, login && !LoginForm && onLoginClick && /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    className: cx('user_info'),
    onClick: onLoginClick
  }, getTestId('userInfo', 'button')), toggler), (LoginForm || !login && ProfileInfo) && /*#__PURE__*/React.createElement("div", {
    className: cx('user_info')
  }, /*#__PURE__*/React.createElement(Popover, {
    isOpen: isUserInfoOpen,
    toggler: toggler,
    onToggle: setIsUserInfoOpen,
    align: "right",
    isMobileFixed: true,
    namespace: getNamespace()
  }, function (_ref2) {
    var height = _ref2.height;
    var Element = login ? LoginForm : ProfileInfo;
    var elemProps = {
      onClose: function onClose() {
        if (isFunc(setIsUserInfoOpen)) {
          setIsUserInfoOpen(false);
        }
      },
      style: {
        height: height
      }
    };

    if ( /*#__PURE__*/isValidElement(Element)) {
      return /*#__PURE__*/cloneElement(Element, elemProps);
    }

    return null;
  })));
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("nav", _extends({
    ref: headerRef,
    className: cx('root', "theme_".concat(theme), {
      fixed: fixed
    })
  }, getTestId()), /*#__PURE__*/React.createElement("div", {
    className: cx('top', classes.top)
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('wrapper')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('left_side')
  }, BackArrow, (navigation === null || navigation === void 0 ? void 0 : navigation.length) > 0 && /*#__PURE__*/React.createElement("button", _extends({
    type: "button",
    onClick: handleMenuToggle,
    className: cx('button', 'tablet', 'mobile', classes.burger),
    title: openMenuTitle
  }, getTestId('toggle', 'button')), isMobileMenu || isNavLinksOpen ? /*#__PURE__*/React.createElement(CloseLinear, null) : /*#__PURE__*/React.createElement(Burger, {
    size: "md"
  })), /*#__PURE__*/React.createElement("a", _extends({
    href: linkHome,
    className: cx('home_link'),
    title: headerTitle
  }, getNewTabProps(!!linkHomeInNewTab), linkHomeProps, getTestId('homeLink')), /*#__PURE__*/React.createElement("span", {
    className: cx('logo_wrapper')
  }, ' ', logo || /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("span", {
    className: cx('logo', 'desktop', 'tablet', {
      is_search: isSearch && isTablet
    })
  }, /*#__PURE__*/React.createElement(Logo, {
    classes: {
      root: cx('logo_icon')
    }
  })), /*#__PURE__*/React.createElement("span", {
    className: cx('logo', 'mobile')
  }, /*#__PURE__*/React.createElement(LogoMobile, {
    classes: {
      root: cx('logo_icon')
    }
  }))))), (!isSearch && navigation || isNavLinksOpen) && /*#__PURE__*/React.createElement("div", {
    className: cx({
      desktop: !isNavLinksOpen,
      tablet_nav_links: isNavLinksOpen
    }, classes.top)
  }, /*#__PURE__*/React.createElement(NavLinks, {
    activeItem: showDropdownMenu ? currentPopup : undefined,
    classes: {
      point: classes.navLinksPoint,
      marker: classes.marker
    },
    items: navigation === null || navigation === void 0 ? void 0 : navigation.map(function (_ref3) {
      var title = _ref3.title,
          titleProps = _ref3.titleProps,
          href = _ref3.href,
          as = _ref3.as,
          isDropDown = _ref3.isDropDown,
          items = _ref3.items,
          target = _ref3.target,
          active = _ref3.active;
      return {
        title: title,
        href: href,
        titleProps: titleProps,
        as: as,
        isDropDown: isDropDown,
        items: items,
        target: target,
        active: active
      };
    }),
    onItemClick: handleItemClick,
    theme: theme,
    namespace: getNamespace(),
    searchOpen: isSearch && isTablet
  })), isSearch && /*#__PURE__*/React.createElement("div", {
    className: cx('search_wrapper')
  }, /*#__PURE__*/React.createElement(ClickOutside, {
    className: cx('search_inner'),
    onClickOutside: handleCloseSearch
  }, /*#__PURE__*/React.createElement(HeaderSearch, _extends({}, searchProps, {
    onSubmit: handleOnSubmit,
    onClose: handleCloseSearch,
    namespace: getNamespace(),
    value: searchValue,
    onChange: handleChangeSearch,
    loading: searchLoading,
    placeholder: searchPlaceholder,
    notFoundTitle: searchNotFoundTitle,
    notFoundSubtitle: searchNotFoundSubtitle,
    notFound: searchNotFound,
    results: searchResults,
    navigationResult: navigationResult,
    theme: theme,
    tablet: isTablet
  }))))), /*#__PURE__*/React.createElement("div", {
    className: cx('right_side')
  }, rightSide || /*#__PURE__*/React.createElement(React.Fragment, null, showSearch && /*#__PURE__*/React.createElement("div", {
    className: cx('section', 'desktop', {
      tablet: isNavLinksOpen
    })
  }, /*#__PURE__*/React.createElement("button", _extends({}, searchButtonProps, {
    onClick: handleToggleSearch,
    className: cx('search_button', classes.rightIcon),
    type: "button"
  }, getTestId('search', 'button')), /*#__PURE__*/React.createElement(Search, null))), !isNavLinksOpen && CartButton, /*#__PURE__*/React.createElement("div", {
    className: cx('desktop', 'region_wrap', {
      mobile: theme === 'b2b' || showMobileAddons,
      tablet: !isNavLinksOpen
    })
  }, /*#__PURE__*/React.createElement(Region, null)), profileBlock, oneWorldLink && /*#__PURE__*/React.createElement("a", _extends({
    href: linkOneWorld,
    className: cx('oneworld_link')
  }, getNewTabProps(!!linkOneWorldInNewTab), getTestId('oneWorld')), "OneWorld", /*#__PURE__*/React.createElement(Oneworld, null)))))), /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: cx('navigation_content_main'),
    timeout: 300,
    in: !isMobile && showDropdownMenu,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement(SwitchTransition, null, /*#__PURE__*/React.createElement(CSSTransition, {
    key: currentPopup,
    classNames: cx('navigation_content'),
    timeout: 0
  }, /*#__PURE__*/React.createElement(ClickOutside, _extends({
    ref: contentRef,
    onClickOutside: closeNavPopup,
    className: cx('navigation_content', classes.navigationContent),
    ignoreRefs: ignoreRefs
  }, getTestId('navigationContent')), /*#__PURE__*/React.createElement(Scrollbar, {
    classes: {
      root: cx('navigation_content_inner')
    }
  }, currentPopup === 0 && (navigationFavorites === null || navigationFavorites === void 0 ? void 0 : (_navigationFavorites$ = navigationFavorites.items) === null || _navigationFavorites$ === void 0 ? void 0 : _navigationFavorites$.length) > 0 && /*#__PURE__*/React.createElement("div", {
    className: cx('content_wrap')
  }, /*#__PURE__*/React.createElement("div", {
    className: cx('favorites', 'favorites_common')
  }, /*#__PURE__*/React.createElement(Favorites, {
    namespace: getNamespace(),
    items: navigationFavorites === null || navigationFavorites === void 0 ? void 0 : navigationFavorites.items
  }))), /*#__PURE__*/React.createElement("div", {
    className: cx('content_wrap')
  }, (favorites === null || favorites === void 0 ? void 0 : favorites.length) > 0 && /*#__PURE__*/React.createElement("div", {
    className: cx('favorites')
  }, /*#__PURE__*/React.createElement(Favorites, {
    items: favorites,
    separatedItems: true,
    namespace: getNamespace()
  })), currentPopup !== null && (navigation === null || navigation === void 0 ? void 0 : (_navigation$currentPo = navigation[currentPopup]) === null || _navigation$currentPo === void 0 ? void 0 : _navigation$currentPo.items) && /*#__PURE__*/React.createElement("div", {
    className: cx('wrapper_popup')
  }, /*#__PURE__*/React.createElement(Menu, {
    content: navigation[currentPopup].content,
    items: (_navigation$currentPo2 = navigation[currentPopup]) === null || _navigation$currentPo2 === void 0 ? void 0 : _navigation$currentPo2.items,
    theme: theme,
    namespace: getNamespace()
  })))), /*#__PURE__*/React.createElement(ButtonBase, {
    className: cx('close_button'),
    onClick: closeNavPopup,
    namespace: getNamespace('close')
  }, /*#__PURE__*/React.createElement(ChevronSmallUp, null)))))), /*#__PURE__*/React.createElement(CSSTransition, {
    classNames: cx('mobile_menu'),
    in: isTablet && isMobileMenu,
    nodeRef: mobileMenuRef,
    timeout: 500,
    unmountOnExit: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: mobileMenuRef,
    className: cx('mobile_menu'),
    style: tabletMobileMenuStyles
  }, /*#__PURE__*/React.createElement(MobileMenu, {
    hint: mobileMenuHint,
    home: mobileMenuHome,
    navigationFavorites: navigationFavorites,
    onClose: handleMenuClose,
    showSearch: showSearch,
    theme: theme,
    Region: Region,
    language: languageBase,
    searchValue: searchValue,
    onChangeSearch: handleChangeSearch,
    isSearch: isSearch,
    searchLoading: searchLoading,
    searchPlaceholder: searchPlaceholder,
    searchNotFoundTitle: searchNotFoundTitle,
    searchNotFoundSubtitle: searchNotFoundSubtitle,
    searchNotFound: searchNotFound,
    navigationResult: navigationResult,
    mobileSelectedGroup: mobileSelectedGroup,
    setMobileSubMenu: setMobileSubMenu,
    searchResults: searchResults,
    profileBlock: profileBlock,
    isShowHeader: !isHideMobileMenuHeader,
    classes: {
      inner: classes.mobileMenu
    }
  })))), fixed && /*#__PURE__*/React.createElement("div", {
    style: {
      height: fixedHeaderHeight
    }
  }), /*#__PURE__*/React.createElement("div", {
    className: cx('layer', {
      layer_visible: showDropdownMenu
    })
  }));
});